'use client';
import React, { useState, useEffect, useRef } from 'react';
import { Clock, AlertTriangle, CheckCircle2, XCircle, ChevronRight } from 'lucide-react';
import { PracticeResult } from './PracticeSessionScreen';

interface Question {
  id: string;
  typeCode: string;
  typeLabel: string;
  difficultyLevel: string;
  adaptiveReason: string;
  passageExtract: string;
  question: string;
  options: string[] | null;
  correctAnswer: string;
  wordLimit?: number;
  explanation: string;
  distractorTrap: string;
}

interface Props {
  question: Question;
  questionNumber: number;
  totalQuestions: number;
  sessionResults: PracticeResult[];
  onResult: (result: PracticeResult) => void;
}

export default function PracticeQuestion({ question, questionNumber, totalQuestions, sessionResults, onResult }: Props) {
  const [answer, setAnswer] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [confidence, setConfidence] = useState(0);
  const [timeSpent, setTimeSpent] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    setAnswer('');
    setSubmitted(false);
    setConfidence(0);
    setTimeSpent(0);
    timerRef.current = setInterval(() => setTimeSpent((p) => p + 1), 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [question.id]);

  const handleSubmit = () => {
    if (!answer) return;
    if (timerRef.current) clearInterval(timerRef.current);
    setSubmitted(true);
  };

  const isCorrect = submitted && (
    question.options
      ? answer === question.correctAnswer || answer.charAt(0) === question.correctAnswer.charAt(0)
      : answer.toLowerCase().trim() === question.correctAnswer.toLowerCase().trim()
  );

  const handleNext = () => {
    onResult({
      questionId: question.id,
      correct: isCorrect,
      timeSpent,
      confidence,
      distractorTrap: !isCorrect ? question.distractorTrap : undefined,
    });
  };

  const sessionAccuracy = sessionResults.length > 0
    ? Math.round((sessionResults.filter((r) => r.correct).length / sessionResults.length) * 100)
    : null;

  const timerWarning = timeSpent >= 80 && timeSpent < 90;
  const timerDanger = timeSpent >= 90;

  return (
    <div className="min-h-screen bg-background">
      {/* Top bar */}
      <div className="sticky top-0 z-10 bg-card border-b border-border px-5 py-2.5 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-foreground">Question {questionNumber} / {totalQuestions}</span>
          <span className={`badge-qtype border ${question.typeCode === 'MATCH_HEAD' ? 'bg-danger-bg text-danger border-danger/30' : question.typeCode === 'YN/NG' ? 'bg-secondary text-primary border-primary/30' : 'bg-warning-bg text-warning border-warning/30'}`}>
            {question.typeLabel}
          </span>
          <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-md">{question.difficultyLevel}</span>
        </div>

        <div className="flex items-center gap-4">
          {/* Session accuracy */}
          {sessionAccuracy !== null && (
            <div className="text-xs text-muted-foreground">
              Session accuracy: <span className={`font-bold tabular-nums ${sessionAccuracy >= 70 ? 'text-accent' : sessionAccuracy >= 55 ? 'text-warning' : 'text-danger'}`}>{sessionAccuracy}%</span>
            </div>
          )}
          {/* Timer */}
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${timerDanger ? 'border-danger/40 bg-danger-bg' : timerWarning ? 'border-warning/40 bg-warning-bg' : 'border-border bg-muted'}`}>
            <Clock size={13} className={timerDanger ? 'text-danger' : timerWarning ? 'text-warning' : 'text-muted-foreground'} />
            <span className={`text-sm font-bold tabular-nums font-mono ${timerDanger ? 'timer-danger' : timerWarning ? 'timer-warning' : 'text-foreground'}`}>
              {String(Math.floor(timeSpent / 60)).padStart(2, '0')}:{String(timeSpent % 60).padStart(2, '0')}
            </span>
            {timerWarning && <span className="text-xs text-warning font-semibold">⚠ 10s</span>}
            {timerDanger && <span className="text-xs text-danger font-semibold">Over target!</span>}
          </div>
        </div>
      </div>

      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 py-6">
        <div className="grid grid-cols-1 xl:grid-cols-3 2xl:grid-cols-3 gap-6">
          {/* Main question area */}
          <div className="xl:col-span-2 space-y-4">
            {/* Adaptive reason */}
            <div className="flex items-center gap-2 text-xs p-2.5 rounded-lg bg-secondary border border-primary/20">
              <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
              <span className="text-muted-foreground">AI focus: </span>
              <span className="font-medium text-foreground">{question.adaptiveReason}</span>
            </div>

            {/* Passage extract */}
            <div className="card-base p-5">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Passage Extract</p>
              <p className="text-sm text-foreground leading-relaxed">{question.passageExtract}</p>
            </div>

            {/* Question */}
            <div className="card-base p-5">
              <p className="text-sm font-semibold text-foreground mb-4 leading-relaxed">{question.question}</p>

              {/* TF/NG or YN/NG */}
              {question.options && (question.typeCode === 'TF/NG' || question.typeCode === 'YN/NG') && (
                <div className="flex gap-3">
                  {question.options.map((opt) => (
                    <button
                      key={`popt-${question.id}-${opt}`}
                      onClick={() => !submitted && setAnswer(opt)}
                      disabled={submitted}
                      className={`flex-1 py-3.5 rounded-xl border-2 text-sm font-semibold transition-all duration-150 ${
                        submitted && opt === question.correctAnswer
                          ? 'border-accent bg-success-bg text-accent'
                          : submitted && opt === answer && !isCorrect
                          ? 'border-danger bg-danger-bg text-danger'
                          : answer === opt && !submitted
                          ? 'border-primary bg-secondary text-primary' :'border-border bg-card text-foreground hover:border-primary/40 disabled:cursor-default'
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              )}

              {/* MCQ */}
              {question.options && question.typeCode === 'MCQ' && (
                <div className="space-y-2.5">
                  {question.options.map((opt) => {
                    const letter = opt.charAt(0);
                    const isSelected = answer === letter;
                    const isCorrectOpt = submitted && question.correctAnswer.charAt(0) === letter;
                    const isWrongSelected = submitted && isSelected && !isCorrect;
                    return (
                      <button
                        key={`popt-${question.id}-${letter}`}
                        onClick={() => !submitted && setAnswer(letter)}
                        disabled={submitted}
                        className={`w-full flex items-start gap-3 p-3.5 rounded-xl border-2 text-left text-sm transition-all duration-150 ${
                          isCorrectOpt ? 'border-accent bg-success-bg text-accent' : isWrongSelected ?'border-danger bg-danger-bg text-danger': isSelected ?'border-primary bg-secondary text-primary': 'border-border bg-card text-foreground hover:border-primary/30 disabled:cursor-default'
                        }`}
                      >
                        <span className={`w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold border-2 mt-0.5 ${
                          isCorrectOpt ? 'bg-accent text-accent-foreground border-accent' :
                          isWrongSelected ? 'bg-danger text-danger-foreground border-danger': isSelected ?'bg-primary text-primary-foreground border-primary': 'border-border text-muted-foreground'
                        }`}>{letter}</span>
                        <span className="leading-relaxed">{opt.slice(3)}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Matching Headings */}
              {question.options && question.typeCode === 'MATCH_HEAD' && (
                <div className="space-y-2">
                  {question.options.map((opt) => {
                    const isSelected = answer === opt;
                    const isCorrectOpt = submitted && question.correctAnswer === opt;
                    const isWrongSelected = submitted && isSelected && !isCorrect;
                    return (
                      <button
                        key={`popt-${question.id}-${opt.slice(0, 15)}`}
                        onClick={() => !submitted && setAnswer(opt)}
                        disabled={submitted}
                        className={`w-full flex items-start gap-3 p-3 rounded-lg border-2 text-left text-xs transition-all duration-150 ${
                          isCorrectOpt ? 'border-accent bg-success-bg text-accent' : isWrongSelected ?'border-danger bg-danger-bg text-danger': isSelected ?'border-primary bg-secondary text-primary': 'border-border bg-card text-foreground hover:border-primary/30 disabled:cursor-default'
                        }`}
                      >
                        <span className={`w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold border-2 mt-0.5 ${
                          isCorrectOpt ? 'bg-accent text-accent-foreground border-accent' :
                          isWrongSelected ? 'bg-danger text-danger-foreground border-danger': isSelected ?'bg-primary text-primary-foreground border-primary': 'border-border text-muted-foreground'
                        }`}>{opt.charAt(0)}</span>
                        <span className="leading-relaxed">{opt.slice(2)}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Completion */}
              {!question.options && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle size={13} className="text-warning" />
                    <p className="text-xs text-warning font-medium">No more than {question.wordLimit} word{(question.wordLimit ?? 1) > 1 ? 's' : ''} — copy spelling directly from the passage</p>
                  </div>
                  <input
                    type="text"
                    value={answer}
                    onChange={(e) => !submitted && setAnswer(e.target.value)}
                    disabled={submitted}
                    placeholder="Your answer"
                    className={`input-field ${submitted ? (isCorrect ? 'border-accent bg-success-bg' : 'border-danger bg-danger-bg') : ''}`}
                  />
                </div>
              )}

              {/* Submit */}
              {!submitted && (
                <button
                  onClick={handleSubmit}
                  disabled={!answer}
                  className="btn-primary mt-4 w-full justify-center disabled:opacity-40"
                >
                  Submit Answer
                </button>
              )}

              {/* Feedback */}
              {submitted && (
                <div className={`mt-4 p-4 rounded-xl border-2 animate-slide-up ${isCorrect ? 'bg-success-bg border-accent/40' : 'bg-danger-bg border-danger/40'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {isCorrect ? (
                      <CheckCircle2 size={16} className="text-accent" />
                    ) : (
                      <XCircle size={16} className="text-danger" />
                    )}
                    <p className={`text-sm font-bold ${isCorrect ? 'text-accent' : 'text-danger'}`}>
                      {isCorrect ? 'Correct!' : `Incorrect — Correct answer: ${question.correctAnswer}`}
                    </p>
                    <span className="ml-auto text-xs text-muted-foreground tabular-nums">{timeSpent}s</span>
                  </div>
                  <p className="text-xs text-foreground leading-relaxed mb-2">{question.explanation}</p>
                  {!isCorrect && (
                    <div className="flex items-start gap-2 p-2.5 rounded-lg bg-warning-bg border border-warning/30 mt-2">
                      <AlertTriangle size={12} className="text-warning flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-warning font-medium">{question.distractorTrap}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Side panel */}
          <div className="space-y-4">
            {/* Session progress */}
            <div className="card-base p-4">
              <p className="text-xs font-semibold text-foreground uppercase tracking-wide mb-3">Session Progress</p>
              <div className="flex items-center gap-2 mb-2">
                <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full transition-all duration-300"
                    style={{ width: `${((questionNumber - 1) / totalQuestions) * 100}%` }}
                  />
                </div>
                <span className="text-xs text-muted-foreground tabular-nums">{questionNumber - 1}/{totalQuestions}</span>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-3">
                {[
                  { id: 'sp-correct', label: 'Correct', value: sessionResults.filter((r) => r.correct).length, color: 'text-accent' },
                  { id: 'sp-wrong', label: 'Wrong', value: sessionResults.filter((r) => !r.correct).length, color: 'text-danger' },
                  { id: 'sp-acc', label: 'Accuracy', value: sessionAccuracy !== null ? `${sessionAccuracy}%` : '—', color: sessionAccuracy !== null && sessionAccuracy >= 58 ? 'text-accent' : 'text-warning' },
                ].map((stat) => (
                  <div key={stat.id} className="text-center p-2 rounded-lg bg-muted">
                    <p className={`text-lg font-bold tabular-nums ${stat.color}`}>{stat.value}</p>
                    <p className="text-xs text-muted-foreground">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Time vs target */}
            <div className="card-base p-4">
              <p className="text-xs font-semibold text-foreground uppercase tracking-wide mb-3">Time vs. Target</p>
              <div className="flex items-end gap-2 mb-1">
                <span className={`text-3xl font-bold tabular-nums ${timerDanger ? 'text-danger' : timerWarning ? 'text-warning' : 'text-foreground'}`}>
                  {timeSpent}s
                </span>
                <span className="text-sm text-muted-foreground mb-1">/ 90s target</span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-300 ${timeSpent > 90 ? 'bg-danger' : timeSpent > 80 ? 'bg-warning' : 'bg-accent'}`}
                  style={{ width: `${Math.min(100, (timeSpent / 90) * 100)}%` }}
                />
              </div>
              {sessionResults.length > 0 && (
                <p className="text-xs text-muted-foreground mt-2">
                  Avg this session: <span className="font-semibold text-foreground tabular-nums">
                    {Math.round(sessionResults.reduce((a, r) => a + r.timeSpent, 0) / sessionResults.length)}s
                  </span>
                </p>
              )}
            </div>

            {/* Confidence rating */}
            {submitted && confidence === 0 && (
              <div className="card-base p-4 animate-slide-up">
                <p className="text-xs font-semibold text-foreground uppercase tracking-wide mb-3">Rate Your Confidence</p>
                <p className="text-xs text-muted-foreground mb-3">How confident were you in this answer? This improves your adaptive set.</p>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={`conf-${n}`}
                      onClick={() => setConfidence(n)}
                      className="flex-1 aspect-square rounded-lg border-2 border-border text-sm font-bold text-muted-foreground hover:border-primary hover:text-primary transition-all duration-150"
                    >
                      {n}
                    </button>
                  ))}
                </div>
                <div className="flex justify-between text-xs text-muted-foreground mt-1.5">
                  <span>Guessed</span>
                  <span>Certain</span>
                </div>
              </div>
            )}

            {confidence > 0 && submitted && (
              <button onClick={handleNext} className="btn-primary w-full justify-center">
                {questionNumber < totalQuestions ? <>Next Question <ChevronRight size={14} /></> : 'View Session Summary →'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}